/**
 * The overtime bank, plan 0027 G2 (owner, 2026-09-23; AML §10-6(12)): the balance, what went in
 * each week and what was taken, worked out by the server from approved overtime and approved
 * Avspasering, and what was adjusted or paid out by hand (slice 3). The same panel on the
 * employee card (the manager, who may also adjust and pay out) and on Min side (the person).
 */
import { errorText } from '@/api/client';
import { type ApiTimeBank, useTimeBank, useTimeBankWrites } from '@/api/data';
import { T } from '@/i18n';
import { today } from '@/rules';
import { Button, Chip, Panel, toast } from '@/ui';
import { DateInput } from '@/ui/DateInput';
import { fmtD, fmtDs, num } from '@/ui/format';
import { isoWeek } from '@timefly/core';
import { useState } from 'react';

const hours = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${num(Math.abs(n), 1)} t`;

export function TimeBankView({ bank }: { bank: ApiTimeBank }) {
  // a backend from before slice 3 sends no entries; read that as none rather than failing
  const entries = bank.entries ?? [];
  if (!bank.agreed_on && !bank.flex && entries.length === 0)
    return <p className="hint">{T('Ingen skriftlig avtale om avspasering. Overtiden betales ut.')}</p>;
  const lines = [
    ...bank.weeks.map((w) => ({
      key: `w${w.week}`,
      when: w.week,
      text: T('Overtid uke {n}', { n: isoWeek(w.week) }),
      h: w.hours_in,
    })),
    ...bank.taken.map((t) => ({
      key: `t${t.id}`,
      when: t.from,
      text:
        t.from === t.to
          ? T('Avspasering {d}', { d: fmtDs(t.from) })
          : T('Avspasering {a} til {b}', { a: fmtDs(t.from), b: fmtDs(t.to) }),
      h: -t.hours,
    })),
    ...(bank.flex?.weeks ?? []).map((w) => ({
      key: `f${w.week}`,
      when: w.week,
      text: T('Fleksitid uke {n}: {w} av {e} t', {
        n: isoWeek(w.week),
        w: num(w.worked, 1),
        e: num(w.expected, 1),
      }),
      h: w.flex,
    })),
    ...entries.map((e) => ({
      key: `e${e.id}`,
      when: e.date,
      text:
        e.kind === 'payout'
          ? T('Utbetalt {d}', { d: fmtDs(e.date) })
          : T('Justert {d}: {note}', { d: fmtDs(e.date), note: e.note ?? '' }),
      h: e.hours,
    })),
  ].sort((a, b) => b.when.localeCompare(a.when));
  return (
    <div data-testid="timebank">
      <dl className="ovdl">
        <dt>{T('Saldo')}</dt>
        <dd>
          {bank.balance < 0 ? (
            <Chip tone="dgr" small>
              {hours(bank.balance)}
            </Chip>
          ) : (
            <b>{num(bank.balance, 1)} t</b>
          )}
        </dd>
        {bank.flex && (
          <>
            <dt>{T('Fleksitid')}</dt>
            <dd>
              {num(bank.flex.hours, 1)} t, {T('fra {d}', { d: fmtD(bank.flex.agreed_on) })}
            </dd>
          </>
        )}
        <dt>{T('Overtid til timebank fra')}</dt>
        <dd>
          {bank.agreed_on ? fmtD(bank.agreed_on) : T('ingen avtale nå')}
          {bank.share != null && bank.share < 100 ? `, ${T('{n} % av overtiden', { n: bank.share })}` : ''}
        </dd>
        <dt>{T('Inn og ut')}</dt>
        <dd>
          +{num(bank.hours_in, 1)} t, −{num(bank.hours_taken, 1)} t
        </dd>
      </dl>
      {lines.length > 0 && (
        <ul className="tblines">
          {lines.map((l) => (
            <li key={l.key}>
              <span>{l.text}</span>
              <b>{hours(l.h)}</b>
            </li>
          ))}
        </ul>
      )}
      <p className="hint">
        {T(
          'Regnet ut fra godkjente vakter og godkjent avspasering. Overtidstillegget betales i måneden timene ble jobbet.',
        )}
      </p>
    </div>
  );
}

/**
 * A manager's two actions (slice 3): an adjustment, up or down with a note (an opening balance
 * from an earlier system, a correction), or a payout of hours from the bank, paid in the pay period
 * that holds its date. Nothing entered is changed later; a mistake is corrected by an adjustment.
 */
function Actions({ id, bank }: { id: number; bank: ApiTimeBank }) {
  const write = useTimeBankWrites();
  const [open, setOpen] = useState<'adjustment' | 'payout' | null>(null);
  const [f, setF] = useState({ hours: '', date: today(), note: '' });
  const start = (kind: 'adjustment' | 'payout') => {
    setOpen(kind);
    setF({
      hours: kind === 'payout' && bank.balance > 0 ? String(bank.balance).replace('.', ',') : '',
      date: today(),
      note: '',
    });
  };
  const save = async () => {
    if (!open) return;
    try {
      await write.mutateAsync({
        id,
        body: {
          kind: open,
          hours: Number(f.hours.replace(',', '.')),
          date: f.date,
          ...(f.note.trim() ? { note: f.note.trim() } : {}),
        },
      });
      toast(open === 'payout' ? T('Utbetalingen er lagret') : T('Saldoen er justert'));
      setOpen(null);
    } catch (e) {
      toast(errorText(e));
    }
  };
  return (
    <div className="tbact">
      {!open && (
        <div className="df">
          <Button small onClick={() => start('adjustment')}>
            {T('Juster saldo')}
          </Button>
          <Button small onClick={() => start('payout')} disabled={bank.balance <= 0}>
            {T('Utbetal')}
          </Button>
        </div>
      )}
      {open && (
        <div className="fgrid" data-testid="timebank-form">
          <label className="fld2">
            <span>{open === 'payout' ? T('Timer å utbetale') : T('Timer, + eller −')}</span>
            <input
              className="inp"
              inputMode="decimal"
              value={f.hours}
              onChange={(e) => setF({ ...f, hours: e.target.value })}
            />
          </label>
          <label className="fld2" htmlFor="timebank-date">
            <span>{T('Dato')}</span>
            <DateInput
              id="timebank-date"
              className="inp"
              // a payout cannot be backdated into a month already paid (review of 2026-09-24)
              {...(open === 'payout' ? { min: today() } : {})}
              value={f.date}
              onValueChange={(value) => setF({ ...f, date: value })}
            />
          </label>
          <label className="fld2">
            <span>{open === 'payout' ? T('Merknad (valgfritt)') : T('Hvorfor')}</span>
            <input className="inp" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
          </label>
          <p className="hint">
            {open === 'payout'
              ? T(
                  'Timene betales i lønnsperioden datoen ligger i, til grunnsats: tillegget ble betalt da de ble jobbet.',
                )
              : T(
                  'For eksempel en saldo overført fra et tidligere system. Det som er lagret, endres ikke; rett en feil med en ny justering.',
                )}
          </p>
          <div className="df">
            <Button small tone="pri" onClick={save} disabled={write.isPending}>
              {open === 'payout' ? T('Utbetal') : T('Lagre justering')}
            </Button>
            <Button small ghost onClick={() => setOpen(null)}>
              {T('Avbryt')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function TimeBankPanel({ id }: { id: number | 'mine' }) {
  const q = useTimeBank(id);
  return (
    <Panel item title={T('Timebank')}>
      {q.data ? (
        <>
          <TimeBankView bank={q.data} />
          {typeof id === 'number' && <Actions id={id} bank={q.data} />}
        </>
      ) : (
        <p className="hint">{q.isError ? T('Kunne ikke hente timebanken.') : T('Henter…')}</p>
      )}
    </Panel>
  );
}
